import React, { useState, useEffect } from 'react';
import { Modal, ConfirmDialog, EmptyState, ActionBtn, SectionHeader, SearchInput, FormField, StatCard, Pagination } from '../components/common/UI';
import { toShamsi, formatCurrency, daysUntil } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { FaMoneyBillWave } from 'react-icons/fa';
import { MdAdd, MdEdit, MdDelete, MdSchool, MdPrint, MdPeople, MdAccessTime, MdCalendarToday, MdClose } from '../utils/icons';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';

const STATUS_CONFIG = {
  Active:    { label: 'فعال',    color: 'bg-green-100 text-green-700 border border-green-200' },
  Upcoming:  { label: 'آینده',   color: 'bg-blue-100 text-blue-700 border border-blue-200' },
  Completed: { label: 'تکمیل',   color: 'bg-gray-500/20 text-gray-600 border border-gray-500/30' },
  Cancelled: { label: 'لغو شده', color: 'bg-red-100 text-red-700 border border-red-200' },
};

const CATEGORIES = ['ویدیوگرافی', 'عکاسی', 'ادیتینگ', 'رنگبندی', 'صدابرداری', 'موشن گرافیک', 'دیگر'];

const initialForm = {
  title: '', instructor: '', category: 'ویدیوگرافی', duration: '',
  schedule: '', startDate: '', endDate: '', capacity: '',
  price: '', status: 'Upcoming', description: ''
};

// محاسبه‌ی خودکار تاریخ پایان دوره از روی تاریخ شروع + مدت دوره (به روز)
function calcEndDate(startDate, durationDays) {
  if (!startDate || !durationDays) return '';
  const s = new Date(startDate);
  if (isNaN(s.getTime())) return '';
  const days = Number(durationDays);
  if (!days || days <= 0) return '';
  return new Date(s.getTime() + days * 24 * 60 * 60 * 1000);
}

export default function Classes() {
  const { isManager } = useAuth();
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, totalEnrolled: 0, totalCapacity: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [printItem, setPrintItem] = useState(null);
  const [printStudent, setPrintStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [payStudent, setPayStudent] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [newStudent, setNewStudent] = useState({ name: '', phone: '', email: '', fee: '' });
  const [page, setPage] = useState(1); // پیج‌بندی (صفحه‌بندی سمت کلاینت)

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      
      const res = await API.get('/classes', { params });
      setClasses(res.data.data);
      setStats(res.data.stats);
    } catch (error) {
      toast.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, [filterStatus, search]);

  useEffect(() => { setPage(1); }, [filterStatus, search]);

  const openAdd = () => { setForm(initialForm); setEditItem(null); setShowModal(true); };
  const openEdit = (c) => {
    setForm({
      ...c,
      startDate: c.startDate?.split('T')[0] || '',
      endDate: c.endDate?.split('T')[0] || ''
    });
    setEditItem(c);
    setShowModal(true);
  };
  
  const openStudents = (c) => {
    setSelectedClass(c);
    setShowStudentModal(true);
  };

  const handleUpdateStudent = async () => {
    try {
      await API.put(`/classes/${selectedClass._id}/students/${editStudent._id}`, {
        name: editStudent.name,
        fatherName: editStudent.fatherName,
        phone: editStudent.phone,
        email: editStudent.email,
        fee: editStudent.fee,
        paidAmount: editStudent.paidAmount
      });
      toast.success('شاگرد بروزرسانی شد');
      setEditStudent(null);
      fetchClasses();
      const updated = await API.get(`/classes/${selectedClass._id}`);
      setSelectedClass(updated.data.data);
    } catch (err) {
      toast.error('خطا در بروزرسانی');
    }
  };
  
  const handlePayStudent = async () => {
    try {
      const newPaidAmount = (payStudent.paidAmount || payStudent.fee || 0) + Number(paymentAmount);
      await API.put(`/classes/${selectedClass._id}/students/${payStudent._id}`, {
        ...payStudent,
        paidAmount: newPaidAmount
      });
      toast.success('پرداخت ثبت شد');
      setPayStudent(null);
      setPaymentAmount('');
      fetchClasses();
    } catch (err) {
      toast.error('خطا در پرداخت');
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.instructor) return toast.error('عنوان و مدرس ضروری است');
    try {
      if (editItem) {
        await API.put(`/classes/${editItem._id}`, form);
        toast.success('کلاس بروزرسانی شد');
      } else {
        await API.post('/classes', form);
        toast.success('کلاس اضافه شد');
      }
      setShowModal(false);
      fetchClasses();
    } catch (error) {
      toast.error('خطا در ذخیره');
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/classes/${deleteId}`);
      toast.success('کلاس حذف شد');
      setDeleteId(null);
      fetchClasses();
    } catch (error) {
      toast.error('خطا در حذف');
    }
  };

  const handleAddStudent = async () => {
    if (!newStudent.name) return toast.error('نام شاگرد ضروری است');
    try {
      await API.post(`/classes/${selectedClass._id}/students`, newStudent);
      toast.success('شاگرد اضافه شد');
      setNewStudent({ name: '', phone: '', email: '', fee: '' });
      fetchClasses();
      const updated = await API.get(`/classes/${selectedClass._id}`);
      setSelectedClass(updated.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'خطا');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await API.delete(`/classes/${selectedClass._id}/students/${studentId}`);
      toast.success('شاگرد حذف شد');
      fetchClasses();
      const updated = await API.get(`/classes/${selectedClass._id}`);
      setSelectedClass(updated.data.data);
    } catch (error) {
      toast.error('خطا در حذف');
    }
  };

  if (loading && classes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-500">در حال بارگذاری...</p>
      </div>
    );
  }

  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(classes.length / PAGE_SIZE));
  const paged = classes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader
        title="کلاس‌ها و دوره‌ها"
        subtitle={`${classes.length} دوره`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="کلاس جدید" />}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<MdSchool size={18} />} label="کل دوره‌ها" value={stats.total} color="blue" />
        <StatCard icon={<MdSchool size={18} />} label="دوره‌های فعال" value={stats.active} color="green" />
        <StatCard icon={<MdPeople size={18} />} label="کل ثبت‌ نام‌ها" value={stats.totalEnrolled} color="purple" />
        <StatCard icon={<MdPeople size={18} />} label="ظرفیت کل" value={stats.totalCapacity} color="orange"
          sub={`${stats.totalCapacity - stats.totalEnrolled} خالی`} />
      </div>

      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1"><SearchInput value={search} onChange={setSearch} placeholder="جستجوی کلاس، مدرس..." /></div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input sm:w-40">
            <option value="">همه وضعیت‌ها</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {classes.length === 0 ? (
        <EmptyState icon="🎓" title="کلاسی یافت نشد" subtitle="اولین دوره آموزشی را اضافه کنید"
          action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="کلاس جدید" />} />
      ) : (
        <>
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr><th>عنوان دوره</th><th>مدرس</th><th>کتگوری</th><th>مدت</th><th>تاریخ پایان</th><th>ثبت‌نام / ظرفیت</th><th>قیمت</th><th>وضعیت</th><th>عملیات</th></tr>
                </thead>
                <tbody>
                  {paged.map(c => (
                    <tr key={c._id}>
                      <td><div className="font-semibold text-gray-800">{c.title}</div></td>
                      <td className="text-gray-600">{c.instructor}</td>
                      <td><span className="badge bg-blue-100 text-blue-700">{c.category}</span></td>
                      <td className="text-gray-500 text-sm">{c.duration}</td>
                      <td className="text-gray-500 text-sm">{toShamsi(c.endDate)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-800 font-medium">{c.students?.length || 0}/{c.capacity}</span>
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, ((c.students?.length || 0)/c.capacity)*100)}%` }} />
                          </div>
                        </div>
                       </td>
                      <td className="text-green-600 font-medium">{formatCurrency(c.price)}</td>
                      <td><span className={`badge ${STATUS_CONFIG[c.status]?.color}`}>{STATUS_CONFIG[c.status]?.label}</span></td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openStudents(c)} className="p-1.5 text-blue-600 hover:text-blue-700 rounded-lg"><MdAdd size={15} /></button>
                          <button onClick={() => setViewItem(c)} className="p-1.5 text-green-600 hover:text-green-700 rounded-lg"><MdPeople size={15} /></button>
                          {isManager && <button onClick={() => openEdit(c)} className="p-1.5 text-yellow-600 hover:text-yellow-700 rounded-lg"><MdEdit size={15} /></button>}
                          {isManager && <button onClick={() => setDeleteId(c._id)} className="p-1.5 text-red-600 hover:text-red-700 rounded-lg"><MdDelete size={15} /></button>}
                        </div>
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {paged.map(c => (
              <div key={c._id} className="mobile-card">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-gray-800">{c.title}</div>
                    <div className="text-xs text-gray-500">{c.instructor} • {c.category}</div>
                  </div>
                  <span className={`badge ${STATUS_CONFIG[c.status]?.color}`}>{STATUS_CONFIG[c.status]?.label}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                  <div><span className="text-gray-600">مدت:</span> {c.duration}</div>
                  <div><span className="text-gray-600">ثبت‌نام:</span> {c.students?.length || 0}/{c.capacity}</div>
                  <div><span className="text-gray-600">قیمت:</span> {formatCurrency(c.price)}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openStudents(c)} className="flex-1 py-1.5 text-xs bg-blue-100 text-blue-600 rounded-lg">شاگردان</button>
                  {isManager && <button onClick={() => openEdit(c)} className="flex-1 py-1.5 text-xs bg-gray-50 text-gray-800 rounded-lg">ویرایش</button>}
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش کلاس' : 'کلاس جدید'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="عنوان دوره" required><input className="form-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="مثلاً: دوره فیلم‌برداری مقدماتی" /></FormField>
          <FormField label="مدرس" required><input className="form-input" value={form.instructor} onChange={e => setForm({...form, instructor: e.target.value})} placeholder="نام مدرس" /></FormField>
          <FormField label="کتگوری"><select className="form-input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>{CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></FormField>
          <FormField label="وضعیت"><select className="form-input" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>{Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}</select></FormField>
          <FormField label="مدت دوره (روز)"><input type="number" min="1" className="form-input" value={form.duration} onChange={e => { const duration = e.target.value; setForm(prev => ({...prev, duration, endDate: calcEndDate(prev.startDate, duration)})); }} placeholder="مثلاً: 30" /></FormField>
          <FormField label="برنامه هفتگی"><input className="form-input" value={form.schedule} onChange={e => setForm({...form, schedule: e.target.value})} placeholder="مثلاً: شنبه، دوشنبه، چهارشنبه" /></FormField>
          <FormField label="تاریخ شروع"><ShamsiDatePicker value={form.startDate} onChange={date => setForm(prev => ({...prev, startDate: date, endDate: calcEndDate(date, prev.duration)}))} className="form-input" /></FormField>
          <FormField label="تاریخ پایان (خودکار)">
            <input className="form-input bg-gray-50 cursor-not-allowed text-gray-600" value={form.endDate ? toShamsi(form.endDate) : ''} readOnly placeholder="با انتخاب تاریخ شروع و مدت دوره محاسبه می‌شود" />
          </FormField>
          <FormField label="ظرفیت"><input type="number" className="form-input" value={form.capacity} onChange={e => setForm({...form, capacity: +e.target.value})} placeholder="مثلاً: 20" /></FormField>
          <FormField label="قیمت (افغانی)"><input type="number" className="form-input" value={form.price} onChange={e => setForm({...form, price: +e.target.value})} placeholder="مثلاً: 5000" /></FormField>
          <div className="sm:col-span-2"><FormField label="توضیح"><textarea className="form-input h-20" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="توضیحات دوره (اختیاری)" /></FormField></div>
        </div>
        <div className="flex gap-3 justify-end mt-6">
          <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800">انصراف</button>
          <ActionBtn onClick={handleSave} label={editItem ? 'بروزرسانی' : 'ذخیره'} />
        </div>
      </Modal>

      {/* Students Modal */}
      {selectedClass && (
  <Modal isOpen={showStudentModal} onClose={() => setShowStudentModal(false)} title={`شاگردان کلاس ${selectedClass.title}`} size="lg">
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="text-gray-800 text-sm font-medium mb-3">افزودن شاگرد جدید</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input className="form-input" placeholder="نام کامل" value={newStudent.name} onChange={e => setNewStudent({...newStudent, name: e.target.value})} />
          <input className="form-input" placeholder="تلفن" value={newStudent.phone} onChange={e => setNewStudent({...newStudent, phone: e.target.value})} />
          <input className="form-input" placeholder="ایمیل" value={newStudent.email} onChange={e => setNewStudent({...newStudent, email: e.target.value})} />
          <input className="form-input" placeholder="مبلغ کل (فیس)" type="number" value={newStudent.fee} onChange={e => setNewStudent({...newStudent, fee: e.target.value})} />
          <input className="form-input" placeholder="مبلغ پرداخت شده" type="number" value={newStudent.paidAmount} onChange={e => setNewStudent({...newStudent, paidAmount: e.target.value})} />
        </div>
        <button onClick={handleAddStudent} className="mt-3 w-full py-2 bg-blue-600 text-white rounded-xl text-sm">+ افزودن شاگرد</button>
      </div>
      
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {selectedClass.students?.length === 0 ? (
          <p className="text-gray-600 text-center py-8">هیچ شاگردی ثبت نشده است</p>
        ) : (
          selectedClass.students?.map(s => {
            const remaining = (selectedClass.price || 0) - (s.paidAmount || s.fee || 0);
            const isPaid = remaining <= 0;
            return (
              <div key={s._id} className="flex flex-col p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-gray-800 font-medium">{s.name}</div>
                    <div className="text-xs text-gray-500">{s.phone} {s.email && `• ${s.email}`}</div>
                    <div className="text-xs mt-1">
                      <span className="text-gray-600">پرداخت شده: </span>
                      <span className="text-green-600">{formatCurrency(s.paidAmount || s.fee || 0)}</span>
                      <span className="text-gray-600 mx-2">|</span>
                      <span className="text-gray-600">باقی‌مانده: </span>
                      <span className={remaining > 0 ? "text-red-600" : "text-green-600"}>
                        {formatCurrency(remaining > 0 ? remaining : 0)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setEditStudent(s)} 
                      className="p-1.5 text-blue-600 hover:text-blue-700 transition-colors"
                      title="ویرایش شاگرد"
                    >
                      <MdEdit size={16} />
                    </button>
                    <button 
                      onClick={() => setPrintStudent({ class: selectedClass, student: s })} 
                      className="p-1.5 text-purple-600 hover:text-purple-700 transition-colors"
                      title="چاپ کارت شاگرد"
                    >
                      <MdPrint size={16} />
                    </button>
                    {remaining > 0 && (
                      <button 
                        onClick={() => setPayStudent(s)} 
                        className="p-1.5 text-green-600 hover:text-green-700 transition-colors"
                        title="پرداخت باقی‌مانده"
                      >
                        <FaMoneyBillWave size={16} />
                      </button>
                    )}
                    <button 
                      onClick={() => handleRemoveStudent(s._id)} 
                      className="p-1.5 text-red-600 hover:text-red-700 transition-colors"
                    >
                      <MdClose size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  </Modal>
)}

{/* مودال ویرایش شاگرد */}
{/* مودال ویرایش شاگرد */}
{editStudent && (
  <Modal isOpen={!!editStudent} onClose={() => setEditStudent(null)} title="ویرایش شاگرد" size="md">
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input 
          className="form-input" 
          placeholder="نام کامل" 
          value={editStudent.name} 
          onChange={e => setEditStudent({...editStudent, name: e.target.value})} 
        />
        <input 
          className="form-input" 
          placeholder="نام پدر" 
          value={editStudent.fatherName || ''} 
          onChange={e => setEditStudent({...editStudent, fatherName: e.target.value})} 
        />
        <input 
          className="form-input" 
          placeholder="شماره تماس" 
          value={editStudent.phone} 
          onChange={e => setEditStudent({...editStudent, phone: e.target.value})} 
        />
        <input 
          className="form-input" 
          placeholder="ایمیل" 
          value={editStudent.email || ''} 
          onChange={e => setEditStudent({...editStudent, email: e.target.value})} 
        />
        <input 
          className="form-input" 
          placeholder="مبلغ کل (فیس)" 
          type="number" 
          value={editStudent.fee || 0} 
          onChange={e => setEditStudent({...editStudent, fee: Number(e.target.value)})} 
        />
        <input 
          className="form-input" 
          placeholder="مبلغ پرداخت شده" 
          type="number" 
          value={editStudent.paidAmount || 0} 
          onChange={e => setEditStudent({...editStudent, paidAmount: Number(e.target.value)})} 
        />
      </div>
      <div className="bg-gray-50 rounded-lg p-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">باقی‌مانده:</span>
          <span className={(editStudent.fee - editStudent.paidAmount) > 0 ? "text-red-600" : "text-green-600"}>
            {formatCurrency((editStudent.fee || 0) - (editStudent.paidAmount || 0))}
          </span>
        </div>
      </div>
      <div className="flex gap-3 justify-end">
        <button onClick={() => setEditStudent(null)} className="px-4 py-2 bg-gray-100 rounded-lg">انصراف</button>
        <button onClick={handleUpdateStudent} className="px-4 py-2 bg-blue-600 rounded-lg">ذخیره تغییرات</button>
      </div>
    </div>
  </Modal>
)}

{/* مودال پرداخت باقی‌مانده */}
{payStudent && (
  <Modal isOpen={!!payStudent} onClose={() => setPayStudent(null)} title="پرداخت باقی‌مانده" size="sm">
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-gray-800">شاگرد: {payStudent.name}</div>
        <div className="text-red-600 text-xl font-bold mt-2">
          {formatCurrency((selectedClass?.price || 0) - (payStudent.paidAmount || payStudent.fee || 0))}
        </div>
        <div className="text-gray-500 text-sm">باقی‌مانده قابل پرداخت</div>
      </div>
      <input 
        type="number" 
        className="form-input" 
        placeholder="مبلغ پرداختی" 
        value={paymentAmount} 
        onChange={e => setPaymentAmount(e.target.value)}
      />
      <div className="flex gap-3 justify-end">
        <button onClick={() => setPayStudent(null)} className="px-4 py-2 bg-gray-100 rounded-lg">انصراف</button>
        <button onClick={handlePayStudent} className="px-4 py-2 bg-green-600 rounded-lg">تأیید پرداخت</button>
      </div>
    </div>
  </Modal>
)}

      {/* View Modal */}
      {viewItem && (
        <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="جزئیات دوره" size="md">
          <div className="space-y-4">
            <div className="flex justify-between"><h2 className="text-xl font-bold text-gray-800">{viewItem.title}</h2><span className={`badge ${STATUS_CONFIG[viewItem.status]?.color}`}>{STATUS_CONFIG[viewItem.status]?.label}</span></div>
            <div className="grid grid-cols-2 gap-4">
              {[['مدرس', viewItem.instructor], ['کتگوری', viewItem.category], ['مدت', viewItem.duration], ['برنامه', viewItem.schedule], ['شروع', toShamsi(viewItem.startDate)], ['پایان', toShamsi(viewItem.endDate)], ['ظرفیت', viewItem.capacity], ['ثبت‌نام', viewItem.students?.length || 0], ['قیمت', formatCurrency(viewItem.price)]].map(([l, v]) => v && (<div key={l}><div className="text-xs text-gray-600">{l}</div><div className="text-gray-800 text-sm">{v}</div></div>))}
            </div>
            {viewItem.description && <div><div className="text-xs text-gray-600">توضیح</div><p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-xl">{viewItem.description}</p></div>}
          </div>
        </Modal>
      )}

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف کلاس" message="آیا مطمئن هستید؟" />
{/* Print Single Student Modal */}
{printStudent && (
  <Modal isOpen={!!printStudent} onClose={() => setPrintStudent(null)} title="کارت شاگرد" size="md">
    <div id="print-student-content" className="space-y-4">
      {/* کارت شاگرد */}
      <div className="text-center border-b pb-4">
        <h2 className="text-xl font-bold text-gray-800">{printStudent.class.title}</h2>
        <p className="text-gray-500 text-sm">کارت ثبت‌نام شاگرد</p>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-gray-600">نام شاگرد:</span> <span className="text-gray-800 font-bold">{printStudent.student.name}</span></div>
          <div><span className="text-gray-600">تلفن:</span> <span className="text-gray-800">{printStudent.student.phone || '---'}</span></div>
          <div><span className="text-gray-600">ایمیل:</span> <span className="text-gray-800">{printStudent.student.email || '---'}</span></div>
          <div><span className="text-gray-600">تاریخ ثبت:</span> <span className="text-gray-800">{toShamsi(printStudent.student.enrolledAt)}</span></div>
        </div>
      </div>

      <div className="bg-gray-50 p-3 rounded-xl space-y-2 text-sm">
        <div><span className="text-gray-600">نام دوره:</span> <span className="text-gray-800">{printStudent.class.title}</span></div>
        <div><span className="text-gray-600">مدرس:</span> <span className="text-gray-800">{printStudent.class.instructor}</span></div>
        <div><span className="text-gray-600">مدت دوره:</span> <span className="text-gray-800">{printStudent.class.duration}</span></div>
        <div><span className="text-gray-600">برنامه:</span> <span className="text-gray-800">{printStudent.class.schedule}</span></div>
        <div><span className="text-gray-600">تاریخ شروع:</span> <span className="text-gray-800">{toShamsi(printStudent.class.startDate)}</span></div>
        <div><span className="text-gray-600">تاریخ پایان:</span> <span className="text-gray-800">{toShamsi(printStudent.class.endDate)}</span></div>
        {/* فیس پرداختی شاگرد - جایگزین قیمت دوره */}
        <div><span className="text-gray-600">مبلغ پرداختی:</span> <span className="text-green-600 font-bold">{formatCurrency(printStudent.student.fee || printStudent.class.price)}</span></div>
        <div className="text-xs text-gray-500 mt-1">👆 این مبلغ توسط شاگرد پرداخت شده است</div>
      </div>

      {/* بخش امضا */}
      <div className="flex justify-between mt-4 pt-2">
        <div className="text-center">
          <div className="text-xs text-gray-600">امضاء شاگرد</div>
          <div className="mt-6 border-t border-gray-500 w-28 pt-1 text-xs text-gray-500">.........................</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-600">امضاء مسئول</div>
          <div className="mt-6 border-t border-gray-500 w-28 pt-1 text-xs text-gray-500">.........................</div>
        </div>
      </div>

      <div className="text-center text-gray-600 text-xs pt-4 border-t mt-2">
        <p>تاریخ چاپ: {toShamsi(new Date())}</p>
      </div>
    </div>

    <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
      <button onClick={() => setPrintStudent(null)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800">بستن</button>
      <button 
        onClick={() => {
          const printContent = document.getElementById('print-student-content').innerHTML;
          const originalContent = document.body.innerHTML;
          document.body.innerHTML = printContent;
          window.print();
          document.body.innerHTML = originalContent;
          window.location.reload();
        }}
        className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white"
      >
        چاپ کارت 🖨️
      </button>
    </div>
  </Modal>
)}
    </div>
  );
}